'use client';

import { Button } from '@finn/design-system/atoms/button';
import { If } from '@finn/design-system/atoms/If';
import { InfoOutlined } from '@finn/design-system/icons/info-outlined';
import {
  BannerModule,
  BannerModuleDescription,
  BannerModuleIcon,
  BannerModuleTitle,
} from '@finn/design-system/modules/BannerModule';
import { App, parseToHtml, useCurrentApp, useIntl } from '@finn/ui-utils';
import { useWatch } from 'react-hook-form';

import { Skeleton } from '../../../components/Skeleton';
import { useCosmicModule } from '../../../hooks/useCosmicModule';
import { ReturnMetadata } from '../../../types/base';
import { FeatureFormField } from '../../FeatureForm';
import { Grid } from '../../Grid/Grid';
import { GridRow } from '../../Grid/GridRow';
import { TextBlock } from '../../TextBlock';
import { prepareWizardStep } from '../../Wizard';

interface ReturnTypeSelectionStepProps {
  returnMetadata?: ReturnMetadata;
  isLoading: boolean;
  shouldShowNotAvailableMessageForSelfReturn?: boolean;
}

const UnavailabilityBanner = ({
  className,
  title,
  description,
  cta,
}: {
  className?: string;
  title?: string;
  description?: string;
  cta?: { label?: string; href?: string };
}) => {
  const app = useCurrentApp();

  return (
    <BannerModule className={className}>
      <BannerModuleIcon>
        <InfoOutlined />
      </BannerModuleIcon>
      <BannerModuleTitle>{title}</BannerModuleTitle>
      {description ? (
        <BannerModuleDescription>
          {parseToHtml(description)}
        </BannerModuleDescription>
      ) : null}
      {cta ? (
        <Button
          href={app === App.B2B ? 'tel:+4989143770064' : (cta?.href ?? '')}
          variant="link"
          size="md"
        >
          {cta?.label}
        </Button>
      ) : null}
    </BannerModule>
  );
};

const isSelfReturnNotAvailableBecauseOfSwap = () => {
  return false;
};

const ReturnTypeSelectionStep = ({
  returnMetadata,
  isLoading,
  shouldShowNotAvailableMessageForSelfReturn,
}: ReturnTypeSelectionStepProps) => {
  const return_type = useWatch({
    name: 'return_type',
  });
  const content = useCosmicModule('advanced-edit-return');
  const { formatNumber } = useIntl();
  const returnFee = returnMetadata?.return_fee;

  const { selfReturn, pickupReturn } =
    content?.location_selection?.returnPreferences ?? {};

  const pickupReturnLabel = pickupReturn?.label || 'Abholung';
  const selfReturnLabel = selfReturn?.label || 'Selbstruckgabe';
  const app = useCurrentApp();

  const returnInstructions =
    content?.location_selection?.instructions?.map((instruction: any) => {
      if (app !== App.B2B) {
        return instruction;
      }

      // small hack to use b2b links
      return instruction?.replaceAll(
        'https://support.finn.com/hc/de-de/articles/8656478446865-Wie-bereite-ich-mein-FINN-Auto-f%C3%BCr-die-R%C3%BCckgabe-den-Fahrzeugtausch-vor',
        'https://support-b2b.finn.com/hc/de/articles/11729656785425-Wie-bereite-ich-mein-FINN-Auto-f%C3%BCr-die-R%C3%BCckgabe-vor'
      );
    }) || [];
  const optionNotAvailableBanner =
    content?.location_selection?.optionNotAvailableBanner || {};
  const optionNotAvailableBecauseOfSwapBanner =
    content?.location_selection?.optionNotAvailableBecauseOfSwapBanner || {};

  const shouldShowNotAvailableLabel =
    return_type === 'SELF_RETURN' && shouldShowNotAvailableMessageForSelfReturn;

  return (
    <div>
      <Skeleton loading={isLoading}>
        <Grid cols={4}>
          <GridRow colsSpan={4}>
            <If condition={isSelfReturnNotAvailableBecauseOfSwap()}>
              <UnavailabilityBanner
                className="mb-4"
                {...optionNotAvailableBecauseOfSwapBanner}
              />
            </If>
            <FeatureFormField
              type="radio"
              name="return_type"
              fullWidth
              radioItems={[
                {
                  label: pickupReturnLabel,
                  value: 'PICKUP',
                  endAdornment: (
                    <p className="body-14-light">
                      {returnFee === 0
                        ? selfReturn?.priceLabel
                        : formatNumber(Number(returnFee), {
                            currency: 'EUR',
                            style: 'currency',
                          })}
                    </p>
                  ),
                },
                {
                  label: selfReturnLabel,
                  value: 'SELF_RETURN',
                  endAdornment: (
                    <p className="body-14-light">{selfReturn?.priceLabel}</p>
                  ),
                },
              ].filter(
                (item) =>
                  item.value !== 'SELF_RETURN' ||
                  !isSelfReturnNotAvailableBecauseOfSwap()
              )}
              variant="card"
              className="md:grid-cols-1"
              orientation="horizontal"
            />
          </GridRow>
          <GridRow colsSpan={4}>
            {shouldShowNotAvailableLabel ? (
              <UnavailabilityBanner {...optionNotAvailableBanner} />
            ) : app === App.B2B ? (
              <Button
                variant="link"
                size="md"
                className="-mt-4 flex"
                href="https://support-b2b.finn.com/hc/de/articles/11729656785425-Wie-bereite-ich-mein-FINN-Auto-f%C3%BCr-die-R%C3%BCckgabe-vor"
              >
                Wie die Rückgabe abläuft
              </Button>
            ) : (
              <>
                <h6 className="global-t6-semibold">
                  {content?.location_selection?.subtitle}
                </h6>
                <ol className="!mb-0 mt-4 list-none space-y-2 pl-0">
                  {returnInstructions?.map?.(
                    (instruction: any, index: number) => (
                      <li key={index} className="mb-2 flex">
                        <strong className="mr-2 body-14-semibold before:hidden after:hidden">
                          {index + 1}
                        </strong>
                        <TextBlock text={instruction} className="flex-1" />
                      </li>
                    )
                  )}
                </ol>
              </>
            )}
          </GridRow>
        </Grid>
      </Skeleton>
    </div>
  );
};

export const returnTypeSelectionStep = ({
  content,
  returnMetadata,
  availabilitiesIsLoading,
  shouldShowNotAvailableMessageForSelfReturn,
}: {
  content?: {
    title?: string;
    footerCta?: string;
  };
  returnMetadata?: ReturnMetadata;
  availabilitiesIsLoading: boolean;
  shouldShowNotAvailableMessageForSelfReturn?: boolean;
}) => {
  return prepareWizardStep({
    render: () => (
      <ReturnTypeSelectionStep
        returnMetadata={returnMetadata}
        shouldShowNotAvailableMessageForSelfReturn={
          shouldShowNotAvailableMessageForSelfReturn
        }
        isLoading={availabilitiesIsLoading}
      />
    ),
    title: content?.title,
    footer: {
      cta: {
        action: 'next-step',
        label: content?.footerCta,
        disabled: shouldShowNotAvailableMessageForSelfReturn
          ? {
              type: 'equal',
              value: 'SELF_RETURN',
              field: '{form_data.return_type}',
            }
          : undefined,
      },
    },
    fields: [
      {
        name: 'return_type',
        type: 'radio',
        defaultValue: 'PICKUP',
      },
    ],
  });
};
